'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { PropsWithChildren, useEffect, useLayoutEffect, useState } from 'react';

import { intervalSocketBunja } from '@flipster/socket/r230522/interval/socket';
import { Styles } from '@flipster/styled-system/css';

import { CustomHeader } from './custom-header';
import { Candles } from './custom-header/candles';
import { Fullscreen } from './custom-header/fullscreen';
import { Indicators } from './custom-header/indicators';
import { WebIntervals } from './custom-header/intervals';
import { Plugins } from './custom-header/plugins';
import { EventPlugin } from './custom-header/plugins/event';
import { HistoryPlugin } from './custom-header/plugins/history';
import { PositionsPlugin } from './custom-header/plugins/positions';
import { TpSlPlugin } from './custom-header/plugins/tp-sl';
import { TriggerOrdersPlugin } from './custom-header/plugins/trigger-orders';
import { WhaleTrackerPlugin } from './custom-header/plugins/whale-tracker';
import { PriceType, persistentPriceTypeAtom } from './custom-header/price-type';
import { QuickStudies } from './custom-header/quick-studies';
import { Screenshot } from './custom-header/screenshot';
import { getResolveSymbolInfoBaseFn } from './misc/data';
import { createCompositeSymbolName } from './misc/helper';
import { widgetOptions } from './options';
import { datafeed } from './options/datafeed';
import { getDisabledFeatures, getEnabledFeatures } from './options/features';
import { drawEconomicEventMarks } from './options/timescaleMarks';
import { SetupDrawing } from './plugins/setup-drawing';
import { SetupHighLowPrice } from './plugins/setup-high-low-price';
import { TradingViewScope, intervalDataBunja, reconnectionLongTermBunja } from './socket/effect';
import TradingView from './trading-view';
import { TvLayout } from './tv-layout';

export interface TvChartWebProps {
  symbol: string;
  isSpot?: boolean;
  isLoggedIn?: boolean;
  fullscreen?: boolean;
  css?: Styles;
  onWhaleTrackerCopyClick?: (entryPrice: string) => void;
}

const TvChartWeb = ({
  symbol,
  isSpot,
  isLoggedIn,
  fullscreen,
  css: cssProps,
  onWhaleTrackerCopyClick,
}: PropsWithChildren<TvChartWebProps>) => {
  const [tv, setTV] = useState<TradingView>();
  const priceType = useAtomValue(persistentPriceTypeAtom);

  useBunja(intervalDataBunja);
  useBunja(reconnectionLongTermBunja, [TradingViewScope.bind(tv)]);
  const { subscribeSocketIntervalData, unsubscribeSocketIntervalData, requestIntervalSnapshot } =
    useBunja(intervalSocketBunja);

  // TV Chart에게 symbol 정보를 가져오는 방법과 인터벌 데이터 구독/해지하는 방법 주입
  useLayoutEffect(() => {
    datafeed.resolveSymbolInfoBase = getResolveSymbolInfoBaseFn('api');
    datafeed.requestIntervalSnapshot = requestIntervalSnapshot;
    datafeed.subscribeSocketIntervalData = subscribeSocketIntervalData;
    datafeed.unsubscribeSocketIntervalData = unsubscribeSocketIntervalData;
    datafeed.subscribeGetTimescaleMark = drawEconomicEventMarks;
  }, [requestIntervalSnapshot, subscribeSocketIntervalData, unsubscribeSocketIntervalData]);

  const symbolPriceType = isSpot ? 'MID' : priceType;

  useEffect(() => {
    if (tv) return;
    setTV(
      new TradingView({
        symbol: createCompositeSymbolName(symbol, symbolPriceType),
        custom_css_url: './styles.css?v=5',
        enabled_features: getEnabledFeatures('web'),
        disabled_features: getDisabledFeatures('web'),
        datafeed,
        fullscreen: false,
        debug: false,
        ...widgetOptions,
      }),
    );
  }, [symbol, symbolPriceType, tv]);

  useEffect(() => {
    tv?.setSymbol(createCompositeSymbolName(symbol, symbolPriceType));
  }, [symbol, symbolPriceType, tv]);

  const isShowWhaleTracker = onWhaleTrackerCopyClick !== undefined && !isSpot;
  return (
    <>
      <SetupDrawing tv={tv} />
      <SetupHighLowPrice tv={tv} />
      <TvLayout
        tv={tv}
        customHeader={
          <>
            <CustomHeader.HeaderSection>
              <WebIntervals />
              <Candles />
              <Indicators />
              <QuickStudies />
              <Plugins>
                {!isSpot && isLoggedIn && <PositionsPlugin />}
                {!isSpot && isLoggedIn && <TpSlPlugin />}
                {!isSpot && isLoggedIn && <TriggerOrdersPlugin />}
                {isLoggedIn && <HistoryPlugin />}
                <EventPlugin />
                {isShowWhaleTracker && <WhaleTrackerPlugin symbol={symbol} onCopyClick={onWhaleTrackerCopyClick} />}
              </Plugins>
            </CustomHeader.HeaderSection>
            <CustomHeader.HeaderSection>
              {!isSpot && <PriceType />}
              <Screenshot />
              {!fullscreen && <Fullscreen />}
            </CustomHeader.HeaderSection>
          </>
        }
        fullscreen={fullscreen}
        css={cssProps}
      />
    </>
  );
};

export default TvChartWeb;
